import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { authAPI } from './api';
import { useAuthStore } from '../stores/authStore';

/**
 * Info banners that can be closed for good. The choice lives in localStorage (instant, same device) and in the
 * profile (users.dismissedNotices, every device). Ids must match /^[a-z0-9][a-z0-9_-]{0,39}$/.
 */
export const NOTICE_IDS = {
  campaignsAuthors: 'dash-campaigns-authors',
} as const;

const key = (id: string, legacy?: string) => legacy || `gbr_notice_${id}`;
const readLocal = (k: string) => {
  try { return localStorage.getItem(k) === '1'; } catch { return false; }
};

/** `visible` stays false until local or server state is known, so a closed banner never flashes up on a new device. */
export function useNotice(id: string, legacyKey?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const k = key(id, legacyKey);
  const [local, setLocal] = useState(() => readLocal(k));
  const { data, isError } = useQuery({
    queryKey: ['me-notices'],
    queryFn: authAPI.getMe,
    enabled: isAuthenticated && !local,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const serverHas = !!data?.data?.dismissedNotices?.includes(id);
  if (serverHas && !local) {
    try { localStorage.setItem(k, '1'); } catch { /* ignore */ }
  }
  const dismiss = useCallback(() => {
    try { localStorage.setItem(k, '1'); } catch { /* ignore */ }
    setLocal(true);
    api.post('/auth/notices/dismiss', { id }).catch(() => undefined);
  }, [k, id]);
  const hidden = local || serverHas;
  const ready = hidden || !!data || isError;
  return { hidden, visible: ready && !hidden, dismiss };
}
